import { BooleanInput } from '@angular/cdk/coercion';
import { Directive, OnInit, booleanAttribute, computed, input, signal } from '@angular/core';
import { UiAccordionDirective } from '../accordion.directive';
import { injectUiAccordionState } from '../accordion.state';
import { UiAccordionContentDirective } from '../content/accordion-content.directive';
import { UiAccordionTriggerDirective } from '../trigger/accordion-trigger.directive';
import { provideUiAccordionItemState, uiAccordionItemState } from './accordion-item.state';

/**
 * Apply the `uiAccordionItem` directive to an element that represents an accordion item
 */
@Directive({
    selector: '[uiAccordionItem]',
    exportAs: 'uiAccordionItem',
    providers: [provideUiAccordionItemState()],
    host: {
        '[attr.data-orientation]': 'accordion().orientation()',
        '[attr.data-open]': 'state.open() ? "" : null',
        '[attr.data-disabled]': 'state.disabled() || accordion().disabled() ? "" : null'
    }
})
export class UiAccordionItemDirective<T> implements OnInit {
    /**
     * The value of the accordion item
     */
    readonly value = input<T>(undefined, {
        alias: 'uiAccordionItemValue'
    });

    /**
     * Whether the accordion item is disabled
     */
    readonly disabled = input<boolean, BooleanInput>(false, {
        alias: 'uiAccordionItemDisabled',
        transform: booleanAttribute
    });

    /**
     * Access the accordion trigger
     * @internal
     */
    readonly trigger = signal<UiAccordionTriggerDirective<T> | undefined>(undefined);

    /**
     * Access the accordion content
     * @internal
     */
    readonly content = signal<UiAccordionContentDirective<T> | undefined>(undefined);

    /**
     * Whether the accordion item is expanded
     */
    readonly open = computed<boolean>(() => {
        const value = this.state.value();

        if (!value) {
            return false;
        }

        return this.accordion().isOpen(value);
    });
    /**
     * The trigger id
     */
    readonly triggerId = computed(() => this.trigger()?.id());

    /**
     * The content id
     */
    readonly contentId = computed(() => this.content()?.id());

    /**
     * Access the accordion
     */
    protected readonly accordion = injectUiAccordionState<UiAccordionDirective<T>>();

    /**
     * The accordion item state
     */
    private readonly state = uiAccordionItemState<UiAccordionItemDirective<T>>(this);

    ngOnInit(): void {
        if (!this.state.value()) {
            throw new Error('The accordion item value is required');
        }
    }
}
